import { createElement } from "react";
import { Link, router } from "@inertiajs/react";
import { AdminLayout } from "../../../layouts/admin-layout";

interface Item {
  id: number;
  website_id: number;
  url: string | null;
  status: string;
  deploy_id: number | null;
  error_detail: string | null;
}

interface FleetDeploy {
  id: number;
  reason: string;
  status: string;
  total_count: number;
  failed_count: number;
  triggered_by_email: string | null;
  parent_id: number | null;
  created_at: string | null;
  any_failed: boolean;
  retry_url: string;
  items: Item[];
}

interface Props {
  fleet_deploy: FleetDeploy;
  can_trigger: boolean;
}

const STATUS_CLASSES: Record<string, string> = {
  completed: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-800",
  deploying: "bg-blue-100 text-blue-800",
  pending: "bg-gray-100 text-gray-600",
  partial: "bg-amber-100 text-amber-800",
  running: "bg-blue-100 text-blue-800",
  empty: "bg-gray-100 text-gray-600",
};

function StatusBadge({ status }: { status: string }) {
  const cls = STATUS_CLASSES[status] || "bg-gray-100 text-gray-700";
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {status}
    </span>
  );
}

export default function FleetDeployShow({ fleet_deploy, can_trigger }: Props) {
  const fleet = fleet_deploy;

  const retryFailed = () => {
    if (!confirm(`Redeploy ${fleet.failed_count} failed site(s)?`)) return;
    router.post(fleet.retry_url);
  };

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8">
      <Link href="/admin/fleet_deploys" className="text-sm text-blue-600 hover:underline">
        ← All fleet deploys
      </Link>

      <div className="mt-2 flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{fleet.reason}</h1>
          <div className="mt-1 flex items-center gap-3 text-sm text-gray-500">
            <StatusBadge status={fleet.status} />
            <span>{fleet.total_count} sites</span>
            {fleet.failed_count > 0 && (
              <span className="font-semibold text-red-600">{fleet.failed_count} failed</span>
            )}
            {fleet.parent_id && (
              <Link href={`/admin/fleet_deploys/${fleet.parent_id}`} className="text-blue-600 hover:underline">
                retry of #{fleet.parent_id}
              </Link>
            )}
            <span>{fleet.created_at ? new Date(fleet.created_at).toLocaleString() : ""}</span>
          </div>
        </div>

        {can_trigger && fleet.any_failed && (
          <button
            onClick={retryFailed}
            className="rounded-md bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700"
          >
            Redeploy from failed ({fleet.failed_count})
          </button>
        )}
      </div>

      <div className="mt-6 overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              {["Site", "Status", "Deploy", "Error"].map((h) => (
                <th key={h} className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {fleet.items.map((item) => (
              <tr key={item.id}>
                <td className="px-4 py-2 text-sm">
                  {item.url ? (
                    <a href={item.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                      {item.url}
                    </a>
                  ) : (
                    <span className="text-gray-400">website #{item.website_id} (no url)</span>
                  )}
                </td>
                <td className="px-4 py-2"><StatusBadge status={item.status} /></td>
                <td className="px-4 py-2 text-sm text-gray-500">
                  {item.deploy_id ? `#${item.deploy_id}` : "—"}
                </td>
                <td className="px-4 py-2 text-sm text-red-600">
                  {item.error_detail && (
                    <pre className="max-w-md overflow-x-auto whitespace-pre-wrap text-xs">{item.error_detail}</pre>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

FleetDeployShow.layout = (page: React.ReactNode) => createElement(AdminLayout, null, page);
